// Grade model representing climbing difficulty
export class Grade {
  gradeNr: number;
  yds?: string;
  french?: string;
  points?: number;
  a1Points?: number;

  constructor(
    gradeNr: number,
    yds?: string,
    french?: string,
    points?: number,
    a1Points?: number
  ) {
    this.gradeNr = gradeNr;
    this.yds = yds;
    this.french = french;
    this.points = points;
    this.a1Points = a1Points;
  }

  // Method to convert Grade to Firestore data
  toFirestore() {
    return {
      gradeNr: this.gradeNr,
      yds: this.yds,
      french: this.french,
      points: this.points,
      a1Points: this.a1Points
    };
  }

  // Static method to create Grade from Firestore data
  static fromFirestore(data: any): Grade {
    return new Grade(
      data.gradeNr,
      data.yds,
      data.french,
      data.points,
      data.a1Points
    );
  }
}

// Enum for grade systems
export enum GradeSystem {
  FRENCH = 'FRENCH',
  YDS = 'YDS'
}

// Enum for climbing types
export enum ClimbingType {
  ROPES = 'ROPES',
  BOULDER = 'BOULDER'
}

// Performance types for a climb
export enum ClimbPerformance {
  NOT_TOPPED = 'NOT_TOPPED',
  TOPPED = 'TOPPED',
  REDPOINT = 'REDPOINT',
  ON_SIGHT = 'ON_SIGHT',
  FLASH = 'FLASH',
  BLOCKS = 'BLOCKS'
}

// Mapping functions for grade system conversion
export const gradeSystemToStorageValueMap: Record<GradeSystem, string> = {
  [GradeSystem.FRENCH]: 'french',
  [GradeSystem.YDS]: 'yds'
};

export const gradeSystemToStorageValue = (gradeSystem: GradeSystem): string => {
  return gradeSystemToStorageValueMap[gradeSystem];
};

export const gradeSystemFromStorageValue = (storageValue: string): GradeSystem => {
  return Object.keys(gradeSystemToStorageValueMap).find(
    key => gradeSystemToStorageValueMap[key as GradeSystem] === storageValue
  ) as GradeSystem;
};

// Grade system extensions
export const getGradeSystemCrossAxisCount = (gradeSystem: GradeSystem): number => {
  switch (gradeSystem) {
    case GradeSystem.FRENCH:
      return 6;
    case GradeSystem.YDS:
      return 6;
    default:
      throw new Error('Unimplemented grade system');
  }
};

export const getGradeSystemExample = (gradeSystem: GradeSystem): string => {
  switch (gradeSystem) {
    case GradeSystem.FRENCH:
      return "(6a, 6a+, 6b...)";
    case GradeSystem.YDS:
      return "(5.10a, 5.10b, 5.10c...)";
    default:
      throw new Error('Unimplemented grade system');
  }
};

export const getGradeSystemAsString = (gradeSystem?: GradeSystem): string => {
  switch (gradeSystem) {
    case GradeSystem.YDS:
      return "YDS";
    default:
      return "French";
  }
};

// Grade data
export const grades: Grade[] = [
  new Grade(0, '3-4', '1', 1),
  new Grade(1, '5.0', '2', 1),
  new Grade(2, '5.1', '2', 1),
  new Grade(3, '5.2', '3', 1),
  new Grade(4, '5.3', '3', 1),
  new Grade(5, '5.4', '4a', 1),
  new Grade(6, '5.5', '4b', 1),
  new Grade(7, '5.6', '4c', 1),
  new Grade(8, '5.7', '5a', 2),
  new Grade(9, '5.8', '5b', 3),
  new Grade(10, '5.9', '5c', 4),
  new Grade(11, '5.10a', '6a', 6),
  new Grade(12, '5.10b', '6a+', 7),
  new Grade(13, '5.10c', '6b', 8),
  new Grade(14, '5.10d', '6b+', 9),
  new Grade(15, '5.11a', '6c',10),
  new Grade(16, '5.11b', '6c+', 11),
  new Grade(17, '5.11c', '6c+', 11),
  new Grade(18, '5.11d', '7a', 12),
  new Grade(19, '5.12a', '7a+', 14),
  new Grade(20, '5.12b', '7b', 16),
  new Grade(21, '5.12c', '7b+', 18),
  new Grade(22, '5.12d', '7c', 20),
  new Grade(23, '5.13a', '7c+', 22),
  new Grade(24, '5.13b', '8a', 24),
  new Grade(25, '5.13c', '8a+', 28),
  new Grade(26, '5.13d', '8b', 32),
  new Grade(27, '5.14a', '8b+', 36),
  new Grade(28, '5.14b', '8c', 40),
  new Grade(29, '5.14c', '8c+', 44),
  new Grade(30, '5.14d', '9a', 48),
  new Grade(31, '5.15a', '9a+', 56),
  new Grade(32, '5.15b', '9b', 64),
  new Grade(33, '5.15c', '9b+', 72),
  new Grade(34, '5.15d', '9c', 80),
];

export const gradesFrench: Grade[] = [
  new Grade(0, undefined, '1'),
  new Grade(1, undefined, '2'),
  new Grade(3, undefined, '3'),
  new Grade(5, undefined, '4a'),
  new Grade(6, undefined, '4b'),
  new Grade(7, undefined, '4c'),
  new Grade(8, undefined, '5a'),
  new Grade(9, undefined, '5b'),
  new Grade(10, undefined, '5c'),
  new Grade(11, undefined, '6a'),
  new Grade(12, undefined, '6a+'),
  new Grade(13, undefined, '6b'),
  new Grade(14, undefined, '6b+'),
  new Grade(15, undefined, '6c'),
  new Grade(16, undefined, '6c+'),
  new Grade(18, undefined, '7a'),
  new Grade(19, undefined, '7a+'),
  new Grade(20, undefined, '7b'),
  new Grade(21, undefined, '7b+'),
  new Grade(22, undefined, '7c'),
  new Grade(23, undefined, '7c+'),
  new Grade(24, undefined, '8a'),
  new Grade(25, undefined, '8a+'),
  new Grade(26, undefined, '8b'),
  new Grade(27, undefined, '8b+'),
  new Grade(28, undefined, '8c'),
  new Grade(29, undefined, '8c+'),
  new Grade(30, undefined, '9a'),
  new Grade(31, undefined, '9a+'),
  new Grade(32, undefined, '9b'),
  new Grade(33, undefined, '9b+'),
  new Grade(34, undefined, '9c'),
];

export const gradesYDS: Grade[] = [
  new Grade(0, '3-4'),
  new Grade(1, '5.0'),
  new Grade(2, '5.1'),
  new Grade(3, '5.2'),
  new Grade(4, '5.3'),
  new Grade(5, '5.4'),
  new Grade(6, '5.5'),
  new Grade(7, '5.6'),
  new Grade(8, '5.7'),
  new Grade(9, '5.8'),
  new Grade(10, '5.9'),
  new Grade(11, '5.10a'),
  new Grade(12, '5.10b'),
  new Grade(13, '5.10c'),
  new Grade(14, '5.10d'),
  new Grade(15, '5.11a'),
  new Grade(16, '5.11b'),
  new Grade(17, '5.11c'),
  new Grade(18, '5.11d'),
  new Grade(19, '5.12a'),
  new Grade(20, '5.12b'),
  new Grade(21, '5.12c'),
  new Grade(22, '5.12d'),
  new Grade(23, '5.13a'),
  new Grade(24, '5.13b'),
  new Grade(25, '5.13c'),
  new Grade(26, '5.13d'),
  new Grade(27, '5.14a'),
  new Grade(28, '5.14b'),
  new Grade(29, '5.14c'),
  new Grade(30, '5.14d'),
  new Grade(31, '5.15a'),
  new Grade(32, '5.15b'),
  new Grade(33, '5.15c'),
  new Grade(34, '5.15d'),
];

export const getGradesList = (gradeSystem: GradeSystem): Grade[] => {
  switch (gradeSystem) {
    case GradeSystem.FRENCH:
      return gradesFrench;
    case GradeSystem.YDS:
      return gradesYDS;
    default:
      return gradesFrench;
  }
};

export const getGradeAsString = (grade: number, gradeSystem: GradeSystem): string => {
  switch (gradeSystem) {
    case GradeSystem.FRENCH: {
      const foundGrade = grades.find(element => element.gradeNr === grade);
      return foundGrade?.french || '';
    }
    case GradeSystem.YDS: {
      const foundGrade = grades.find(element => element.gradeNr === grade);
      return foundGrade?.yds || '';
    }
    default: {
      const foundGrade = grades.find(element => element.gradeNr === grade);
      return foundGrade?.french || '';
    }
  }
};
